import { MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import { useState } from 'react';

export default function Contact() {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', email: '', message: '' });
        }, 3000);
    };

    return (
        <div className="min-h-screen pt-24 pb-20">
            <section className="bg-gradient-to-br from-emerald-800 to-emerald-900 text-white py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-5xl font-bold mb-6">
                            Contact Us
                        </h1>
                        <p className="text-xl text-emerald-50">
                            Have questions? We're here to help. Reach out to our team anytime.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
                        <div>
                            <h2 className="text-3xl font-bold text-emerald-900 mb-6">Get In Touch</h2>

                            {submitted ? (
                                <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                    <h3 className="text-2xl font-bold text-emerald-900 mb-2">Thank You!</h3>
                                    <p className="text-gray-600">We'll get back to you as soon as possible.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg">
                                    <div className="mb-6">
                                        <label className="block text-emerald-900 font-semibold mb-2">Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                            placeholder="Your full name"
                                        />
                                    </div>

                                    <div className="mb-6">
                                        <label className="block text-emerald-900 font-semibold mb-2">Email</label>
                                        <input
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                            placeholder="your.email@example.com"
                                        />
                                    </div>

                                    <div className="mb-6">
                                        <label className="block text-emerald-900 font-semibold mb-2">Message</label>
                                        <textarea
                                            required
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            rows={5}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                            placeholder="How can we help you?"
                                        ></textarea>
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full bg-yellow-500 text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-400 transition transform hover:scale-105"
                                    >
                                        Send Message
                                    </button>
                                </form>
                            )}
                        </div>

                        <div>
                            <h2 className="text-3xl font-bold text-emerald-900 mb-6">Contact Information</h2>

                            <div className="space-y-6 mb-8">
                                <div className="bg-white p-6 rounded-xl shadow-lg">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-emerald-100 p-3 rounded-lg">
                                            <MapPin className="w-6 h-6 text-emerald-800" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-emerald-900 mb-2">Ethiopia Office</h3>
                                            <p className="text-gray-600">Bole Road, Near Edna Mall</p>
                                            <p className="text-gray-600">Addis Ababa, Ethiopia</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-lg">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-emerald-100 p-3 rounded-lg">
                                            <MapPin className="w-6 h-6 text-emerald-800" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-emerald-900 mb-2">US Office</h3>
                                            <p className="text-gray-600">123 Financial District</p>
                                            <p className="text-gray-600">Washington, DC 20001</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-lg">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-emerald-100 p-3 rounded-lg">
                                            <Phone className="w-6 h-6 text-emerald-800" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-emerald-900 mb-2">Phone</h3>
                                            <p className="text-gray-600">Ethiopia: +251 11 662 1234</p>
                                            <p className="text-gray-600">US: +1 (202) 555-0100</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-lg">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-emerald-100 p-3 rounded-lg">
                                            <Mail className="w-6 h-6 text-emerald-800" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-emerald-900 mb-2">Email</h3>
                                            <p className="text-gray-600">support@mojomoney.com</p>
                                            <p className="text-gray-600">info@mojomoney.com</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-lg">
                                    <div className="flex items-start gap-4">
                                        <div className="bg-emerald-100 p-3 rounded-lg">
                                            <Clock className="w-6 h-6 text-emerald-800" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-emerald-900 mb-2">Business Hours</h3>
                                            <p className="text-gray-600">Monday - Friday: 8:00 AM - 8:00 PM</p>
                                            <p className="text-gray-600">Saturday - Sunday: 9:00 AM - 5:00 PM</p>
                                            <p className="text-emerald-800 font-semibold mt-2">24/7 Emergency Support Available</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-emerald-900 mb-8 text-center">Our Locations</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        <div>
                            <h3 className="text-xl font-bold text-emerald-900 mb-4">Ethiopia Office</h3>
                            <div className="aspect-video rounded-xl overflow-hidden shadow-xl">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.340123456789!2d38.7577!3d9.0320!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zOcKwMDEnNTUuMiJOIDM4wrA0NSczMi44IkU!5e0!3m2!1sen!2sus!4v1234567890"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-bold text-emerald-900 mb-4">US Office</h3>
                            <div className="aspect-video rounded-xl overflow-hidden shadow-xl">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.1234567890!2d-77.0364!3d38.8951!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDUzJzQyLjQiTiA3N8KwMDInMTEuMCJX!5e0!3m2!1sen!2sus!4v1234567890"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
